import { defineStore } from 'pinia';
import { ref } from 'vue';
import { ErpatApiError } from '@erpat/sdk';
import { teamLeavesService } from '@/services/erpat';
import { getSessionEpoch } from '@/services/api/erpat-client';

/**
 * Whether this caller can reach the team LEAVE approval queue, and how much is
 * waiting in it.
 *
 * Syntry keeps NO permit map of its own. The caller's role lives in ERPat web,
 * and the only honest way to ask about it is to call the queue and see what the
 * server says: a 200 means "you may review this", a 403 means the role doesn't
 * cover it, a 404 means the module is off for the tenant (or, for leave, that
 * the endpoints are not deployed yet — see `docs/leave-approval-backend-proposal.md`).
 * Both non-200s are the same answer for our purposes: hide the entry point.
 *
 * Reachability starts FALSE and only ever flips true on a real 200, so every
 * surface fails closed — an employee never sees an approvals link flash in
 * before the answer arrives, and a failed probe leaves it hidden rather than
 * guessing.
 *
 * ONLY a 403/404 is cached, because only those are an answer about this caller.
 * Everything else — a transport failure (`status === 0`, offline), a 500, a
 * timeout, or the CI3 `check_database` hook returning HTML instead of JSON on an
 * under-migrated tenant — means "don't know", and latching that would hide an
 * approver's queue for the rest of the session over one dropped request, with
 * no way back short of relaunching the app. Those leave the store unsettled so
 * the next surface that asks re-probes. Same rule `useAsyncData` uses to tell
 * `unavailable` (403/404) from `offline`.
 *
 * Only LEAVE is probed. Team attendance needs no probe: its page already
 * degrades through `useAsyncData().unavailable`, and both of its entry points
 * (Modules → Attendance, Activity → View all) predate this work and are not
 * gated. Probing it here would cost a request per session that nothing reads.
 *
 * The probe is one row (`per_page: 1`); the count is the server's pagination
 * total, so it is exact rather than the size of the page. Single-flight and
 * cached, because several surfaces ask on the same tick (Modules launcher,
 * Leaves page).
 */
export const useApprovalsStore = defineStore('approvals', () => {
  /** Server said 200 on the team LEAVE queue. False until proven. */
  const leaveReachable = ref(false);
  /** Pending leave applications awaiting this caller. */
  const pendingLeave = ref(0);
  /** True once the probe has settled with a REAL answer (a 200, 403 or 404). */
  const loaded = ref(false);

  // Collapse overlapping probes: the Modules launcher and the Leaves page can
  // both mount within a tick of each other and this must cost one round trip.
  let inFlight: Promise<void> | null = null;
  // Bumped by refresh()/reset(). A run captures it and drops its result if it
  // no longer matches, so the probe abandoned by a refresh can't land AFTER the
  // replacement and overwrite it with a stale verdict.
  let generation = 0;

  /**
   * Probe the queue once per session. Never rejects — a 403/404 IS the answer
   * (not reachable), so callers can `void` this safely.
   */
  function ensureLoaded(): Promise<void> {
    if (loaded.value) return Promise.resolve();
    if (inFlight) return inFlight;

    // Capture the session generation before awaiting: a sign-out that races
    // these probes must not repopulate an approver's queue state onto the next
    // (or guest) session. Same guard the user store and token refresh use.
    const epoch = getSessionEpoch();
    const gen = generation;

    const run = (async () => {
      // Only a definitive verdict is worth caching. Anything else must leave the
      // store unsettled rather than silently pinning "no access" for the session.
      let answered = false;
      const leave = await teamLeavesService
        .list({ status: 'pending', per_page: 1 })
        .catch((err: unknown) => {
          const status = err instanceof ErpatApiError ? err.status : null;
          answered = status === 403 || status === 404;
          return null;
        });

      // Teardown wins: drop a result that resolved after the session changed,
      // or after a refresh()/reset() superseded this run.
      if (getSessionEpoch() !== epoch || gen !== generation) return;

      if (leave) {
        leaveReachable.value = true;
        let waiting = leave.pagination.total || leave.items.length;

        /**
         * A FINAL approver's backlog is not just `pending`.
         *
         * Under dual approval a first approver's "approve" moves the leave to
         * `for_review`, and only a final approver can clear it — so counting
         * `pending` alone showed them a badge of 0 while the work that is
         * uniquely theirs sat waiting. `meta` is the server naming the stage the
         * caller serves; the second probe only runs for the role that needs it.
         *
         * A failure here leaves the pending figure standing rather than
         * discarding a number we already have.
         */
        if (leave.meta.dualApprovalEnabled && leave.meta.isFinalApprover) {
          const endorsed = await teamLeavesService
            .list({ status: 'for_review', per_page: 1 })
            .catch(() => null);

          if (getSessionEpoch() !== epoch || gen !== generation) return;

          if (endorsed) waiting += endorsed.pagination.total || endorsed.items.length;
        }

        pendingLeave.value = waiting;
        answered = true;
      }
      loaded.value = answered;
    })();

    inFlight = run;
    void run.finally(() => {
      if (inFlight === run) inFlight = null;
    });
    return run;
  }

  /**
   * Re-probe from scratch — call after acting on a queue so the pending counts
   * and any newly-granted access are re-read rather than served from the
   * session cache.
   */
  function refresh(): Promise<void> {
    generation += 1;
    loaded.value = false;
    inFlight = null;
    return ensureLoaded();
  }

  /** Drop everything on sign-out; the next user must re-prove their access. */
  function reset(): void {
    generation += 1;
    leaveReachable.value = false;
    pendingLeave.value = 0;
    loaded.value = false;
    inFlight = null;
  }

  return { leaveReachable, pendingLeave, loaded, ensureLoaded, refresh, reset };
});
